<script setup>
import { computed, inject } from 'vue';
import { showcaseFeatures, showcaseHasContent, visitorText } from '../blockContent.js';
import FeatureIcon from '../FeatureIcon.vue';
import { styleVars } from './shared.js';

const props = defineProps({
    config: { type: Object, required: true },
    accent: { type: String, default: '' },
    org: { type: Object, default: () => ({}) },
    site_data: { type: Object, default: () => ({}) },
});

// The design's "Text + image" section: eyebrow, heading, body, then an
// underlined "Our history →" link. `visual_layout` of none drops the
// photo and narrows the column.
const visualLayout = computed(() => props.config.visual_layout || 'right');
// A visitor never meets the photo frame or the block's starter copy:
// with no photo the text column stands alone, and a showcase with
// nothing but its eyebrow draws nothing. The editor keeps both.
const editing = inject('tiqEditorCanvas', false);
const hasVisual = computed(() => visualLayout.value !== 'none' && (editing || !!props.config.visual_image));
const title = computed(() => visitorText(props.config.title, editing));
const body = computed(() => visitorText(props.config.body, editing));
const hasContent = computed(() => editing || showcaseHasContent(props.config));
const pills = computed(() => (Array.isArray(props.config.pills) ? props.config.pills : []));
const features = computed(() => showcaseFeatures(props.config, editing));
</script>

<template>
    <section v-if="hasContent" :id="config.anchor_id || null" class="th-block th-fs" :style="styleVars(config)">
        <div class="th-wrap th-sec">
            <div :class="hasVisual ? 'th-split' : 'th-fs__solo'"
                 class="th-fs__grid"
                 :data-visual="visualLayout">
                <div class="th-fs__content">
                    <p v-if="config.label" class="th-eyebrow">{{ config.label }}</p>
                    <h2 v-if="title" class="th-h2 th-fs__title">{{ title }}</h2>
                    <p v-if="body" class="th-body th-fs__body">{{ body }}</p>

                    <ul v-if="pills.length" class="th-fs__pills">
                        <li v-for="(p, i) in pills" :key="i" class="th-fs__pill">{{ p }}</li>
                    </ul>

                    <ul v-if="features.length" class="th-fs__features">
                        <li v-for="(f, i) in features" :key="i" class="th-fs__feature">
                            <span class="th-fs__icon"><FeatureIcon :name="f.icon || 'bolt'" /></span>
                            <span class="th-fs__feature-text">
                                <strong class="th-fs__feature-title">{{ f.title }}</strong>
                                <span v-if="f.desc" class="th-fs__feature-desc">{{ f.desc }}</span>
                            </span>
                        </li>
                    </ul>

                    <a v-if="config.cta_label" :href="config.cta_href || '#'" class="th-fs__cta">
                        {{ config.cta_label }} <span aria-hidden="true">→</span>
                    </a>
                </div>

                <div v-if="hasVisual" class="th-media th-fs__visual">
                    <img v-if="config.visual_image" :src="config.visual_image" alt="">
                    <span v-else class="th-media__label">Supporting photo</span>
                </div>
            </div>
        </div>
    </section>
</template>

<style scoped>
.th-fs__solo { max-width: 760px; }
.th-fs__grid[data-visual="left"] .th-fs__content { order: 2; }
.th-fs__grid[data-visual="left"] .th-fs__visual { order: 1; }
.th-fs__title { margin-bottom: 16px; }
.th-fs__body { margin-bottom: 14px; }

.th-fs__pills { list-style: none; padding: 0; margin: 18px 0 0; display: flex; flex-wrap: wrap; gap: 8px; }
.th-fs__pill {
    border: 1px solid #d0cdc3;
    border-radius: 999px;
    padding: 7px 14px;
    font-size: 14px;
    font-weight: 600;
}

.th-fs__features { list-style: none; padding: 0; margin: 22px 0 0; }
.th-fs__feature {
    display: grid;
    grid-template-columns: 44px 1fr;
    gap: 14px;
    align-items: flex-start;
    padding: 14px 0;
    border-top: 1px solid var(--th-line);
}
.th-fs__feature:first-child { border-top: 0; padding-top: 0; }
.th-fs__icon {
    width: 44px;
    height: 44px;
    border-radius: var(--r);
    background: var(--tint);
    color: var(--accent);
    display: grid;
    place-items: center;
    padding: 10px;
}
.th-fs__icon :deep(svg) { width: 100%; height: 100%; }
.th-fs__feature-text { display: block; min-width: 0; }
.th-fs__feature-title { display: block; font-size: 16px; font-weight: 600; margin-bottom: 3px; }
.th-fs__feature-desc { display: block; font-size: 15px; color: var(--th-muted); }

/* The design's link treatment: a 2px accent rule under the words. */
.th-fs__cta {
    display: inline-block;
    margin-top: 24px;
    font-weight: 600;
    border-bottom: 2px solid var(--accent);
    text-decoration: none !important;
}
.th-fs__visual { aspect-ratio: 4 / 3; }

@media (max-width: 900px) {
    .th-fs__grid[data-visual="left"] .th-fs__content,
    .th-fs__grid[data-visual="left"] .th-fs__visual { order: initial; }
}
</style>
